import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { CASUAL_GENRE_PROFILE_GENRE_SOURCES } from '../constants';

export const FILTER_TAG_NAME_QUERY_KEY = 'filter_tag_name';
export const FILTERS_KEY_QUERY_KEY = 'filters_key';
export const SHORT_LINK_RESET_FILTER_KEY = 'reset_filter';

const resolveHotTagNameFromFilterValue = (filterValue?: IRecord | null): string => {
  const hotTagValue = filterValue?.[filterKeyMap.HOT_TAGS_SOURCE]?.value;
  if (typeof hotTagValue === 'string') {
    return hotTagValue;
  }

  return hotTagValue?.name || '';
};

const resolveHotTagOptionFromFilterValue = (filterValue?: IRecord | null, hotTagsOptions: IRecord[] = []): IRecord | null => {
  const hotTagName = resolveHotTagNameFromFilterValue(filterValue);
  if (!hotTagName) {
    return null;
  }

  return hotTagsOptions.find((option) => option?.name === hotTagName) || null;
};

export const normalizeCasualGenreProfileFilterValue = (
  filterValue: IRecord | null | undefined,
  defaultFilterValue: IRecord,
  hotTagOption?: IRecord | null,
): IRecord => {
  const filterValueSafe = filterValue || {};
  const { [filterKeyMap.GENRETOR]: genreValueLegacy, [filterKeyMap.GENRETOR_WITH_SEGMENT]: genreValueWithSegment } =
    filterValueSafe;
  const genreValue = genreValueWithSegment || genreValueLegacy;
  const normalizedGenreValue = CASUAL_GENRE_PROFILE_GENRE_SOURCES.includes(genreValue?.source)
    ? genreValue
    : defaultFilterValue?.[filterKeyMap.GENRETOR_WITH_SEGMENT];
  const pageFilterValue = Object.keys(defaultFilterValue || {}).reduce((value, key) => {
    if (filterValueSafe?.[key] === undefined || key === filterKeyMap.GENRETOR_WITH_SEGMENT) {
      return value;
    }

    return {
      ...value,
      [key]: filterValueSafe[key],
    };
  }, {});

  return {
    ...defaultFilterValue,
    ...pageFilterValue,
    [filterKeyMap.GENRETOR_WITH_SEGMENT]: normalizedGenreValue,
    ...(hotTagOption
      ? {
        [filterKeyMap.HOT_TAGS_SOURCE]: {
          value: hotTagOption,
        },
      }
      : {}),
  };
};

export const buildCasualGenreProfileHotTagFilterValue = (params: {
  parsedHotTagFilterValue: IRecord | null | undefined;
  currentFilterValue: IRecord | null | undefined;
  defaultFilterValue: IRecord;
  hotTagOption?: IRecord | null;
}): IRecord => {
  const {
    parsedHotTagFilterValue,
    currentFilterValue,
    defaultFilterValue,
    hotTagOption,
  } = params;
  const nextFilterValue = {
    ...(parsedHotTagFilterValue || {}),
  };

  if (parsedHotTagFilterValue?.[filterKeyMap.ALL_COMPANYS] === undefined) {
    nextFilterValue[filterKeyMap.ALL_COMPANYS] =
      currentFilterValue?.[filterKeyMap.ALL_COMPANYS] || defaultFilterValue?.[filterKeyMap.ALL_COMPANYS];
  }

  return normalizeCasualGenreProfileFilterValue(nextFilterValue, defaultFilterValue, hotTagOption);
};

export const resolveCasualGenreProfileInitialFilterValue = (params: {
  defaultFilterValue: IRecord;
  shortLinkFilterValue?: IRecord | null;
  shortLinkResetFilterValue?: IRecord | null;
  filterTagName?: string;
  hotTagsOptions?: IRecord[];
  parsedHotTagFilterValue?: IRecord | null;
}): { filterValue: IRecord; resetFilterValue: IRecord; hotTagOption: IRecord | null } => {
  const {
    defaultFilterValue,
    shortLinkFilterValue,
    shortLinkResetFilterValue,
    filterTagName,
    hotTagsOptions = [],
    parsedHotTagFilterValue,
  } = params || {};

  if (shortLinkFilterValue && Object.keys(shortLinkFilterValue).length > 0) {
    const shortLinkHotTagOption = resolveHotTagOptionFromFilterValue(shortLinkFilterValue, hotTagsOptions);
    const shortLinkResetHotTagOption = resolveHotTagOptionFromFilterValue(shortLinkResetFilterValue, hotTagsOptions);
    return {
      filterValue: normalizeCasualGenreProfileFilterValue(shortLinkFilterValue, defaultFilterValue, shortLinkHotTagOption),
      resetFilterValue: normalizeCasualGenreProfileFilterValue(
        shortLinkResetFilterValue,
        defaultFilterValue,
        shortLinkResetHotTagOption,
      ),
      hotTagOption: shortLinkHotTagOption,
    };
  }

  if (filterTagName) {
    const matchedOption = hotTagsOptions?.find((option) => option?.name === filterTagName) || null;
    if (matchedOption && parsedHotTagFilterValue && Object.keys(parsedHotTagFilterValue).length > 0) {
      const normalizedHotTagFilterValue = normalizeCasualGenreProfileFilterValue(
        parsedHotTagFilterValue,
        defaultFilterValue,
        matchedOption,
      );
      return {
        filterValue: normalizedHotTagFilterValue,
        resetFilterValue: normalizedHotTagFilterValue,
        hotTagOption: matchedOption,
      };
    }
  }

  return {
    filterValue: defaultFilterValue,
    resetFilterValue: defaultFilterValue,
    hotTagOption: null,
  };
};

export const buildCasualGenreProfileShortLinkSearch = (search: string, shortLinkId: string): string => {
  const query = new URLSearchParams(search?.startsWith('?') ? search.slice(1) : search);
  query.delete(FILTERS_KEY_QUERY_KEY);
  query.delete(FILTER_TAG_NAME_QUERY_KEY);
  query.set(FILTERS_KEY_QUERY_KEY, shortLinkId);

  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
};
